/**
 * PeoplesDock — Public Career Page
 * Route: /careers/:slug (no auth required)
 * Add to App.js: <Route path="/careers/:slug" element={<CareersPage />} />
 *
 * The :slug can be:
 *   - company_domain  (e.g. "techcorp" → techcorp.com)
 *   - company_code    (e.g. "TCH")
 *   - company uuid
 *
 * How to get your slug: Company Settings → Company Code or Domain
 * Share as: https://app.peoplesdock.com/careers/YOUR_CODE
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container, Row, Col, Card, Button, Badge,
    Modal, Form, Spinner, Alert,
} from 'react-bootstrap';
import {
    MapPin, Briefcase, IndianRupee, Clock,
    Send, Upload, FileText, X, ChevronRight, Building2, Globe,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

// ── ALLOWED FILE TYPES ────────────────────────────────────────────────────────
const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const ALLOWED_EXTS  = ['.pdf', '.doc', '.docx'];
const MAX_SIZE_MB   = 5;

const fmt = (n) => n ? `₹${parseInt(n).toLocaleString('en-IN')}` : null;

// ─── Apply Modal ──────────────────────────────────────────────────────────────
export const ApplyModal = ({ show, onHide, job, slug, onSuccess }) => {
    const [form, setForm] = useState({
        first_name:   '',
        last_name:    '',
        email:        '',
        phone:        '',
        cover_letter: '',
    });
    const [resumeFile, setResumeFile] = useState(null);
    const [fileError,  setFileError]  = useState('');
    const [errors,     setErrors]     = useState({});
    const [submitting, setSub]        = useState(false);

    const set = (k, v) => {
        setForm(p => ({ ...p, [k]: v }));
        if (errors[k]) setErrors(p => ({ ...p, [k]: '' }));
    };

    // ── Resume file validation ────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError('');
        setResumeFile(null);

        if (!file) return;

        // Check extension
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
            setFileError(`Only PDF, DOC, DOCX files allowed. Got: ${ext}`);
            e.target.value = '';
            return;
        }

        // Check MIME type
        if (!ALLOWED_TYPES.includes(file.type) && file.type !== '') {
            setFileError('Invalid file type. Please upload a PDF or Word document.');
            e.target.value = '';
            return;
        }

        // Check size
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            setFileError(`File too large. Maximum ${MAX_SIZE_MB} MB allowed.`);
            e.target.value = '';
            return;
        }

        setResumeFile(file);
    };

    const removeFile = () => {
        setResumeFile(null);
        setFileError('');
        // Reset file input
        const input = document.getElementById('resume-upload-input');
        if (input) input.value = '';
    };

    // ── Validation ────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!form.first_name.trim())
            e.first_name = 'First name is required';
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            e.email = 'A valid email address is required';
        return e;
    };

    // ── Submit — uses FormData so file is uploaded ────────────────────────
    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }

        setSub(true);
        try {
            const fd = new FormData();
            fd.append('job_id',       job.id);
            fd.append('first_name',   form.first_name.trim());
            fd.append('last_name',    form.last_name.trim());
            fd.append('email',        form.email.trim().toLowerCase());
            fd.append('phone',        form.phone.trim() || '');
            fd.append('cover_letter', form.cover_letter.trim() || '');

            // Attach file only if selected
            if (resumeFile) {
                fd.append('resume', resumeFile);
            }

            await axiosInstance.post(
                `/ats/public/careers/${slug}/apply`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            toast.success('Application submitted! Check your email for confirmation.');
            onSuccess?.();
            handleClose();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to submit. Please try again.';
            toast.error(msg);
        } finally {
            setSub(false);
        }
    };

    const handleClose = () => {
        setForm({ first_name:'', last_name:'', email:'', phone:'', cover_letter:'' });
        setResumeFile(null);
        setFileError('');
        setErrors({});
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="md" backdrop="static">
            <Modal.Header closeButton className="border-0 pb-0">
                <div>
                    <Modal.Title className="fw-bold fs-5">Apply for this Position</Modal.Title>
                    {job && (
                        <div className="text-muted small mt-1">
                            {job.title}
                            {job.location && <span className="ms-2">· {job.location}</span>}
                        </div>
                    )}
                </div>
            </Modal.Header>

            <Modal.Body className="pt-2">
                <Row className="g-3">

                    {/* Name */}
                    <Col xs={6}>
                        <Form.Label className="small fw-semibold mb-1">
                            First Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            value={form.first_name}
                            onChange={e => set('first_name', e.target.value)}
                            placeholder="Kiran"
                            isInvalid={!!errors.first_name}
                            style={{ borderRadius: 8 }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.first_name}
                        </Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-semibold mb-1">Last Name</Form.Label>
                        <Form.Control
                            value={form.last_name}
                            onChange={e => set('last_name', e.target.value)}
                            placeholder="Kumar"
                            style={{ borderRadius: 8 }}
                        />
                    </Col>

                    {/* Email */}
                    <Col xs={12}>
                        <Form.Label className="small fw-semibold mb-1">
                            Email Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="email"
                            value={form.email}
                            onChange={e => set('email', e.target.value)}
                            placeholder="you@example.com"
                            isInvalid={!!errors.email}
                            style={{ borderRadius: 8 }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.email}
                        </Form.Control.Feedback>
                    </Col>

                    {/* Phone */}
                    <Col xs={12}>
                        <Form.Label className="small fw-semibold mb-1">Phone Number</Form.Label>
                        <Form.Control
                            value={form.phone}
                            onChange={e => set('phone', e.target.value)}
                            placeholder="+91 9876543210"
                            style={{ borderRadius: 8 }}
                        />
                    </Col>

                    {/* ── RESUME UPLOAD ────────────────────────────────── */}
                    <Col xs={12}>
                        <Form.Label className="small fw-semibold mb-1">
                            Resume / CV
                            <span className="text-muted fw-normal ms-1">(PDF, DOC, DOCX · max 5 MB)</span>
                        </Form.Label>

                        {/* Show selected file */}
                        {resumeFile ? (
                            <div
                                className="d-flex align-items-center gap-2 p-2 rounded border"
                                style={{ background: '#f0fdf4', borderColor: '#86efac' }}
                            >
                                <FileText size={18} color="#16a34a" />
                                <span className="small text-truncate flex-grow-1" style={{ maxWidth: 280 }}>
                                    {resumeFile.name}
                                </span>
                                <span className="small text-muted">
                                    {(resumeFile.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <button
                                    type="button"
                                    onClick={removeFile}
                                    className="btn btn-sm p-0 border-0 bg-transparent"
                                    title="Remove file"
                                >
                                    <X size={16} color="#dc2626" />
                                </button>
                            </div>
                        ) : (
                            <div
                                className="border rounded d-flex flex-column align-items-center justify-content-center py-3"
                                style={{
                                    borderStyle: 'dashed',
                                    borderColor: fileError ? '#dc2626' : '#d1d5db',
                                    background: '#f9fafb',
                                    cursor: 'pointer',
                                    position: 'relative',
                                }}
                                onClick={() => document.getElementById('resume-upload-input').click()}
                            >
                                <Upload size={20} color="#6b7280" className="mb-1" />
                                <span className="small text-muted">Click to upload your resume</span>
                                <span className="small" style={{ color: '#9ca3af', fontSize: 11 }}>
                                    PDF, DOC, DOCX up to 5 MB
                                </span>
                                <Form.Control
                                    id="resume-upload-input"
                                    type="file"
                                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        )}

                        {/* File error */}
                        {fileError && (
                            <div className="small text-danger mt-1">
                                <X size={12} className="me-1" />{fileError}
                            </div>
                        )}
                    </Col>

                    {/* Cover letter */}
                    <Col xs={12}>
                        <Form.Label className="small fw-semibold mb-1">
                            Why are you a good fit?
                            <span className="text-muted fw-normal ms-1">(optional)</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.cover_letter}
                            onChange={e => set('cover_letter', e.target.value)}
                            placeholder="Briefly tell us why you'd be great for this role..."
                            style={{ borderRadius: 8, resize: 'none' }}
                            maxLength={1000}
                        />
                        <div className="text-end small text-muted mt-1">
                            {form.cover_letter.length}/1000
                        </div>
                    </Col>

                </Row>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={submit}
                    disabled={submitting || !!fileError}
                    style={{
                        background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                        border: 'none',
                        borderRadius: 8,
                    }}
                >
                    {submitting
                        ? <><Spinner size="sm" className="me-2" />Submitting…</>
                        : <><Send size={14} className="me-2" />Submit Application</>
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CareersPage = () => {
    const { slug } = useParams();
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [selected,   setSelected]   = useState(null); // selected job for detail view
    const [applyModal, setApplyModal] = useState(false);
    const [applied,    setApplied]    = useState(new Set()); // track submitted applications

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        axiosInstance.get(`/ats/public/careers/${slug}`)
            .then(res => setData(res.data.data))
            .catch(err => setError(err.response?.data?.message || 'Career page not found'))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return (
        <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div className="text-center">
                <Spinner animation="border" variant="primary" style={{ width:48, height:48 }}/>
                <p className="text-muted mt-3">Loading careers…</p>
            </div>
        </div>
    );

    if (error || !data) return (
        <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div className="text-center">
                <div style={{ fontSize:64 }}>🔍</div>
                <h4 className="fw-bold mt-3">Page Not Found</h4>
                <p className="text-muted">{error || 'This career page does not exist.'}</p>
            </div>
        </div>
    );

    const { company, jobs } = data;
    const brandColor = company.primary_color || '#4f46e5';

    return (
        <div style={{ minHeight:'100vh', background:'#f8fafc' }}>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div style={{
                background: `linear-gradient(135deg, ${brandColor} 0%, #7c3aed 100%)`,
                padding: '48px 0 32px',
            }}>
                <Container>
                    <div className="text-center">
                        {company.logo_url && (
                            <img src={company.logo_url} alt={company.name}
                                style={{ height:72, objectFit:'contain', marginBottom:16,
                                    filter:'brightness(0) invert(1)', opacity:0.9 }}
                                onError={e => { e.target.style.display='none'; }}/>
                        )}
                        <h1 className="fw-bold text-white mb-2" style={{ fontSize:'2.2rem' }}>
                            {company.name}
                        </h1>
                        {company.tagline && (
                            <p style={{ color:'rgba(255,255,255,0.8)', fontSize:16, marginBottom:12 }}>
                                {company.tagline}
                            </p>
                        )}
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                            {company.website && (
                                <a href={company.website} target="_blank" rel="noreferrer"
                                    style={{ color:'rgba(255,255,255,0.75)', fontSize:13, textDecoration:'none' }}
                                    className="d-flex align-items-center gap-1">
                                    <Globe size={13}/> {company.website.replace(/https?:\/\//, '')}
                                </a>
                            )}
                            <span style={{ color:'rgba(255,255,255,0.75)', fontSize:13 }}
                                className="d-flex align-items-center gap-1">
                                <Briefcase size={13}/> {jobs.length} Open Position{jobs.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                </Container>
            </div>

            {/* ── Content ─────────────────────────────────────────────────── */}
            <Container style={{ padding:'36px 16px' }}>
                {jobs.length === 0 ? (
                    <div className="text-center py-5">
                        <div style={{ fontSize:56 }}>🎯</div>
                        <h5 className="fw-bold mt-3 mb-2">No open positions right now</h5>
                        <p className="text-muted">
                            We don't have any open roles at the moment, but check back soon!
                        </p>
                    </div>
                ) : (
                    <Row className="g-3">
                        {/* Job list */}
                        <Col md={selected ? 5 : 12} lg={selected ? 4 : 12}>
                            <h5 className="fw-bold mb-3">Open Positions</h5>
                            {jobs.map(job => (
                                <Card key={job.id}
                                    onClick={() => setSelected(job)}
                                    className="border-0 shadow-sm mb-2"
                                    style={{
                                        borderRadius:12, cursor:'pointer',
                                        border: selected?.id === job.id
                                            ? `2px solid ${brandColor}` : '1px solid #e5e7eb',
                                        background: selected?.id === job.id ? `${brandColor}08` : '#fff',
                                        transition:'all 0.15s',
                                    }}>
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="fw-bold" style={{ fontSize:15 }}>{job.title}</div>
                                                <div className="d-flex gap-2 flex-wrap mt-1">
                                                    {job.category && (
                                                        <Badge bg="light" text="dark" style={{ fontSize:11 }}>
                                                            {job.category}
                                                        </Badge>
                                                    )}
                                                    {job.employment_type && (
                                                        <Badge bg="light" text="dark" style={{ fontSize:11 }}>
                                                            <Clock size={10} className="me-1"/>
                                                            {job.employment_type}
                                                        </Badge>
                                                    )}
                                                    {applied.has(job.id) && (
                                                        <Badge bg="success" style={{ fontSize:11 }}>
                                                            ✓ Applied
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-muted mt-1" style={{ fontSize:12 }}>
                                                    {job.location && <span><MapPin size={11} className="me-1"/>{job.location}</span>}
                                                    {job.salary_min && (
                                                        <span className="ms-2">
                                                            <IndianRupee size={11} className="me-1"/>
                                                            {fmt(job.salary_min)}
                                                            {job.salary_max ? ` – ${fmt(job.salary_max)}` : '+'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-muted flex-shrink-0 mt-1"/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </Col>

                        {/* Job detail panel */}
                        {selected && (
                            <Col md={7} lg={8}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius:16 }}>
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                                            <div>
                                                <h4 className="fw-bold mb-1">{selected.title}</h4>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {selected.category && (
                                                        <Badge style={{ background: brandColor }}>
                                                            {selected.category}
                                                        </Badge>
                                                    )}
                                                    <Badge bg="light" text="dark">{selected.employment_type || 'Full-Time'}</Badge>
                                                    {selected.location && (
                                                        <Badge bg="light" text="dark">
                                                            <MapPin size={11} className="me-1"/>{selected.location}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {selected.salary_min && (
                                                    <div className="text-muted small mt-1">
                                                        <IndianRupee size={12} className="me-1"/>
                                                        {fmt(selected.salary_min)}
                                                        {selected.salary_max ? ` – ${fmt(selected.salary_max)}` : '+'} per annum
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                disabled={applied.has(selected.id)}
                                                onClick={() => setApplyModal(true)}
                                                style={{
                                                    background: applied.has(selected.id)
                                                        ? '#059669' : `linear-gradient(135deg,${brandColor},#7c3aed)`,
                                                    border:'none', borderRadius:10,
                                                    fontWeight:600,
                                                }}>
                                                {applied.has(selected.id)
                                                    ? '✓ Applied'
                                                    : <><Send size={14} className="me-1"/>Apply Now</>}
                                            </Button>
                                        </div>

                                        <hr/>

                                        {selected.description ? (
                                            <div style={{ fontSize:14, lineHeight:1.8, color:'#374151' }}
                                                dangerouslySetInnerHTML={{
                                                    __html: selected.description
                                                        .replace(/\n/g, '<br/>')
                                                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                                }}/>
                                        ) : (
                                            <p className="text-muted">No description provided for this role.</p>
                                        )}

                                        <Alert variant="light" className="mt-4 small text-center"
                                            style={{ border:'1px solid #e5e7eb', borderRadius:10 }}>
                                            <Building2 size={14} className="me-2 text-primary"/>
                                            Powered by <strong>PeoplesDock</strong> — HR Platform by I2Speed Technologies LLP
                                        </Alert>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                )}
            </Container>

            {/* Apply Modal */}
            {selected && (
                <ApplyModal
                    show={applyModal}
                    onHide={() => setApplyModal(false)}
                    job={selected}
                    slug={slug}
                    onSuccess={() => setApplied(p => new Set([...p, selected.id]))}
                />
            )}
        </div>
    );
};

export default CareersPage;
